import { useEffect, useState } from "react";
import { Plus, Check, Trash2, Pencil, Mail, Phone } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmModal";
import PragmaSelect from "./PragmaSelect";
import ComposeEmailModal from "./ComposeEmailModal";

function fmt(due) {
  if (!due) return "—";
  try { return new Date(due).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return due; }
}

/**
 * MovesTimeline
 * Props:
 *  - linkedType, linkedId: when readOnly is false, used both for filtering and as the link for new moves
 *  - users: list (for assignee selection in the create form)
 *  - readOnly: when true, hides the "Add Move" button + helper text appears via wrapper
 *  - source: optional alternative endpoint (e.g. /contacts/{id}/moves for aggregated read-only)
 */
export default function MovesTimeline({ linkedType, linkedId, users = [], readOnly = false, source, contactEmail, contactPhone }) {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const [moves, setMoves] = useState([]);
  const [types, setTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = async () => {
    try {
      const movesUrl = source || `/moves?linked_type=${linkedType}&linked_id=${linkedId}`;
      const [m, t] = await Promise.all([
        api.get(movesUrl),
        api.get("/move-types"),
      ]);
      setMoves(m.data); setTypes(t.data);
    } catch { toast.error("Failed to load moves"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [linkedType, linkedId, source]);

  const markDone = async (id) => {
    if (readOnly) return;
    try { await api.patch(`/moves/${id}`, { status: "done" }); load(); toast.success("Move done"); }
    catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (readOnly) return;
    if (!await confirm({ title: "Delete this move?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete" })) return;
    await api.delete(`/moves/${id}`);
    load();
  };

  const statusColor = (s) => s === "overdue" ? "var(--pg-red)" : s === "done" ? "var(--pg-accent-deep)" : "var(--pg-text-dim)";

  return (
    <div className="pg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm mono uppercase text-mute tracking-wider">Moves</h3>
        {!readOnly && (
          <div className="flex items-center gap-2">
            {contactPhone && (
              <a
                href={`tel:${String(contactPhone).replace(/[^\d+]/g, "")}`}
                className="pg-btn"
                title={`Call ${contactPhone}`}
                data-testid="moves-call-btn"
              >
                <Phone size={14} /> Call
              </a>
            )}
            {contactEmail && (
              <button
                type="button"
                className="pg-btn"
                onClick={() => setComposeOpen(true)}
                title={`Email ${contactEmail}`}
                data-testid="moves-email-btn"
              >
                <Mail size={14} /> Email
              </button>
            )}
            <button className="pg-btn" onClick={() => setShowForm(true)} data-testid="add-move-btn">
              <Plus size={14} /> Add Move
            </button>
          </div>
        )}
      </div>

      {moves.length === 0 ? (
        <div className="text-mute mono text-xs">No moves yet</div>
      ) : (
        <div className="space-y-2">
          {moves.map((m) => (
            <div key={m.id} className="flex items-start gap-3 p-3" style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
              <button
                onClick={() => m.status !== "done" && markDone(m.id)}
                disabled={readOnly}
                style={{
                  marginTop: 2,
                  color: m.status === "done" ? "var(--pg-accent-deep)" : "var(--pg-text-mute)",
                  cursor: readOnly ? "default" : "pointer",
                  opacity: readOnly ? 0.7 : 1,
                }}
                className={readOnly ? "" : "hover:opacity-80"}
                data-testid={`move-toggle-${m.id}`}
              >
                <Check size={16} />
              </button>
              <div className="flex-1">
                <div className="text-sm" style={{ color: m.status === "done" ? "var(--pg-text-mute)" : "var(--pg-text)", textDecoration: m.status === "done" ? "line-through" : "none" }}>
                  {m.title}
                </div>
                <div className="mono text-[11px] text-mute mt-1">
                  <span style={{ color: statusColor(m.effective_status) }}>{m.effective_status}</span>
                  {" · "}{m.type}{" · "}{fmt(m.due_date)}
                  {readOnly && m.linked_type && (
                    <span className="text-mute"> · linked to {m.linked_type}</span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(m)} className="text-mute hover:text-[var(--pg-accent)]" data-testid={`move-edit-${m.id}`} title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(m.id)} className="text-mute hover:text-[var(--pg-red)]" title="Delete" data-testid={`move-delete-${m.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {readOnly && (
        <div className="mono text-[11px] text-mute mt-4" data-testid="moves-readonly-hint">
          Moves are created inside Leads or Deals.
        </div>
      )}

      {showForm && !readOnly && (
        <NewMoveInline
          linkedType={linkedType}
          linkedId={linkedId}
          types={types}
          users={users}
          defaultAssignee={currentUser?.id}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {editing && !readOnly && (
        <EditMoveInline
          move={editing}
          types={types}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <ComposeEmailModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        defaultTo={contactEmail || ""}
        linkedType={linkedType}
        linkedId={linkedId}
        onDraftChange={() => window.dispatchEvent(new CustomEvent("pragma:drafts-changed"))}
      />
    </div>
  );
}

export function NewMoveInline({ linkedType, linkedId, types, users, onClose, onCreated, defaultAssignee }) {
  const [form, setForm] = useState({
    title: "",
    type: types[0]?.name || "Call",
    due_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    assigned_to: defaultAssignee || users[0]?.id || "",
    notes: "",
    linked_type: linkedType,
    linked_id: linkedId,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, due_date: new Date(form.due_date).toISOString() };
      if (!payload.linked_type) delete payload.linked_type;
      if (!payload.linked_id) delete payload.linked_id;
      await api.post("/moves", payload);
      toast.success("Move added");
      onCreated();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  // From the global Moves page, allow only lead/deal as linked types (Change 1).
  const allowedLinkTypes = linkedType ? null : ["lead", "deal"];

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="move-form-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">New Move</h2>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Title</label>
            <input className="pg-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="move-input-title" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Type</label>
            <PragmaSelect
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v })}
              options={types.map((t) => ({ value: t.name, label: t.name }))}
              testId="move-input-type"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Due Date / Time</label>
            <input className="pg-input" type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required data-testid="move-input-due" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Assigned To</label>
            <PragmaSelect
              value={form.assigned_to}
              onChange={(v) => setForm({ ...form, assigned_to: v })}
              options={users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
            />
          </div>
          {allowedLinkTypes && (
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Linked To</label>
              <PragmaSelect
                value={form.linked_type || ""}
                onChange={(v) => setForm({ ...form, linked_type: v, linked_id: "" })}
                options={[
                  { value: "",     label: "— none —" },
                  { value: "lead", label: "Lead" },
                  { value: "deal", label: "Deal" },
                ]}
                placeholder="— none —"
              />
              <div className="mono text-[11px] text-mute mt-1">Moves are created inside Leads or Deals.</div>
            </div>
          )}
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Notes</label>
            <textarea className="pg-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="move-submit">{saving ? "Saving…" : "Create Move"}</button>
        </div>
      </div>
    </>
  );
}

export function EditMoveInline({ move, types, users, onClose, onSaved }) {
  const toLocal = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toISOString().slice(0, 16); }
    catch { return ""; }
  };
  const [form, setForm] = useState({
    title: move.title || "",
    type: move.type || (types[0]?.name || "Call"),
    due_date: toLocal(move.due_date),
    assigned_to: move.assigned_to || (users[0]?.id || ""),
    notes: move.notes || "",
    status: move.status || "pending",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        type: form.type,
        due_date: new Date(form.due_date).toISOString(),
        assigned_to: form.assigned_to,
        notes: form.notes,
        status: form.status,
      };
      await api.patch(`/moves/${move.id}`, payload);
      toast.success("Move updated");
      onSaved();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-testid="move-edit-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">Edit Move</h2>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Title</label>
            <input className="pg-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="move-edit-title" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Type</label>
            <PragmaSelect
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v })}
              options={types.map((t) => ({ value: t.name, label: t.name }))}
              testId="move-edit-type"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Due Date / Time</label>
            <input className="pg-input" type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required data-testid="move-edit-due" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Assigned To</label>
            <PragmaSelect
              value={form.assigned_to}
              onChange={(v) => setForm({ ...form, assigned_to: v })}
              options={users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Status</label>
            <PragmaSelect
              value={form.status}
              onChange={(v) => setForm({ ...form, status: v })}
              options={[
                { value: "pending", label: "Pending" },
                { value: "done",    label: "Done" },
              ]}
              testId="move-edit-status"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Notes</label>
            <textarea className="pg-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="move-edit-submit">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </>
  );
}

